import React, {
  useState,
  useCallback,
  memo,
  useMemo,
  useEffect,
  useRef,
  CSSProperties,
  HTMLProps,
} from 'react';
import {
  Button,
  CircularProgress,
  Stack,
  TableCell,
  TableRow,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  Dialog,
  DialogContent,
  Typography,
  Box,
  Paper,
} from '@mui/material';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import { ItemTableProps } from '../../../types/FolderTypes';
import { useStore } from 'zustand';
import { defaultGlobalItem, useGlobalItemStore } from '../../../stores/globalItemStore';
import { GlobalItem } from '../../../api/protosCompiled/globalItem/globalItem_pb';
import { useDeleteItem, useFetchItemsByFileId } from '../../../hooks/useFetchItemsByFileId';
import { useAssemblyStore } from '../../../stores/AssemblyStore';
import { useHandleMoveItemOrAssemblyFile } from '../folderManager/helpers';
import { useFolderStore } from '../../../stores/folderStore';
import { useUnityBuildStore } from '../../../stores/store';
import { Add, Delete, Edit, DriveFileMove } from '@mui/icons-material';
import { TakeoffAssembly } from '../../../types/AssemblyItemType';
import { ConfirmationDialog } from '../../common/ConfirmationDialog';
import { useQueryClient } from 'react-query';
import { useTheme as useCustomTheme } from '../../../contexts/ThemeContext';
import { useFetchUser } from '../../../hooks/useFetchUsers';
import { PERMISSIONS, usePermissions } from '../../accessManager';

// Memoize the ActionButton component
const ActionButton = memo(
  ({
    onClick,
    icon,
    tooltip,
    theme,
    colorType = 'primary',
  }: {
    onClick: (e: React.MouseEvent<any>) => void;
    icon: React.ReactNode;
    tooltip: string;
    theme: any;
    colorType?: 'primary' | 'error';
  }) => (
    <Tooltip title={tooltip} arrow>
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          color: theme.palette.text.secondary,
          transition: 'all 0.2s ease',
          '&:hover': {
            color: colorType === 'error' ? theme.palette.error.main : theme.palette.primary.main,
            backgroundColor: alpha(
              colorType === 'error' ? theme.palette.error.main : theme.palette.primary.main,
              0.1
            ),
          },
        }}
        aria-label={tooltip}
      >
        {icon}
      </IconButton>
    </Tooltip>
  )
);
ActionButton.displayName = 'ActionButton';

// Make this a standalone named function that can be exported
export const sortAttributes = (
  a: { attributevaluename: string },
  b: { attributevaluename: string }
) => {
  const aName = a.attributevaluename.trim();
  const bName = b.attributevaluename.trim();

  const aMatch = aName.match(/^(\d+)/);
  const bMatch = bName.match(/^(\d+)/);

  if (aMatch && bMatch) {
    const aNum = parseInt(aMatch[0], 10);
    const bNum = parseInt(bMatch[0], 10);
    if (aNum !== bNum) {
      return aNum - bNum;
    }
  } else if (aMatch) {
    return -1;
  } else if (bMatch) {
    return 1;
  }

  return aName.localeCompare(bName);
};

const ItemTable: React.FC<ItemTableProps> = memo(({ folder }) => {
  const theme = useTheme();
  const { darkMode } = useCustomTheme();
  const listContainerRef = useRef<HTMLDivElement>(null);

  // Extract only what we need from zustand stores using selectors
  const isMoveMode = useStore(useFolderStore, state => state.isMoveMode);
  const setIsMoveMode = useStore(useFolderStore, state => state.setIsMoveMode);
  const selectedBreakout = useStore(useUnityBuildStore, state => state.selectedBreakout);

  const setCurrentItem = useStore(useGlobalItemStore, state => state.setCurrentItem);
  const setIsViewingItemForm = useStore(useGlobalItemStore, state => state.setIsViewingItemForm);
  const setIsCreatingItem = useStore(useGlobalItemStore, state => state.setIsCreatingItem);

  const assemblyOption = useStore(useAssemblyStore, state => state.assemblyOption);
  const currentGlobalAssembly = useStore(useAssemblyStore, state => state.currentGlobalAssembly);
  const currentTakeoffAssembly = useStore(useAssemblyStore, state => state.currentTakeoffAssembly);
  const setIsEditMode = useStore(useAssemblyStore, state => state.setIsEditMode);
  const setCurrentGlobalAssembly = useStore(
    useAssemblyStore,
    state => state.setCurrentGlobalAssembly
  );
  const setCurrentTakeoffAssembly = useStore(
    useAssemblyStore,
    state => state.setCurrentTakeoffAssembly
  );

  //hooks
  const { data: globalItems, isLoading } = useFetchItemsByFileId(folder.folderid, {
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 0, // Always consider data stale to force refresh
  });
  const queryClient = useQueryClient(); // Add queryClient
  const { mutate: deleteItem } = useDeleteItem();
  const moveCurrentItemFile = useHandleMoveItemOrAssemblyFile();

  const { data: currentUser } = useFetchUser();
  const { hasAnyPermission, hasPermission } = usePermissions(currentUser);

  // Refresh data when folder changes
  useEffect(() => {
    if (folder.folderid) {
      queryClient.refetchQueries(['itemsByFileId', folder.folderid], {
        exact: true,
      });
    }
  }, [folder.folderid, queryClient]);

  // Memoize the sortAttributes function
  const memoizedSortAttributes = useCallback(sortAttributes, []);

  // Memoize this derived value
  const setCurrentAssembly = useMemo(
    () => (assemblyOption === 'global' ? setCurrentGlobalAssembly : setCurrentTakeoffAssembly),
    [assemblyOption, setCurrentGlobalAssembly, setCurrentTakeoffAssembly]
  );

  // Local state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<GlobalItem.AsObject | null>(null);
  const [isItemLoading, setIsItemLoading] = useState(false);
  const [loadingItemName, setLoadingItemName] = useState('');

  // Memoize the processed items
  const processedItems = useMemo(() => {
    if (!globalItems) return [];

    return globalItems
      .flatMap((item: GlobalItem.AsObject) => {
        const sortedAttributes = [...item.attributesList].sort(memoizedSortAttributes);
        // When mapping to the new object, store the original index in the attributes array
        return sortedAttributes.map((attribute, index) => {
          // Find the original index of this attribute in the item's attributes list
          const originalAttrIndex = item.attributesList.findIndex(
            attr => attr.attributevalueid === attribute.attributevalueid
          );

          return {
            item,
            attribute,
            // Store both the sorted index and the original index
            assyIdx: originalAttrIndex, // Use original index in the attributes list
            sortedIndex: index, // Also keep track of the sorted position
            uniqueKey: `${item.itemid}-${attribute.attributevalueid}`,
          };
        });
      })
      .sort((a, b) => memoizedSortAttributes(a.attribute, b.attribute));
  }, [globalItems, memoizedSortAttributes]);

  // Memoize handlers
  const handleCancel = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setIsCreatingItem(false);
      setIsMoveMode(false);
      setCurrentAssembly(null);
    },
    [setIsCreatingItem, setIsMoveMode, setCurrentAssembly]
  );

  const handleEdit = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, item: GlobalItem.AsObject) => {
      e.stopPropagation();
      setIsItemLoading(true);
      setLoadingItemName(item.itemname);

      // Set a timeout to simulate loading and to catch actual slow loads
      setTimeout(() => {
        setIsViewingItemForm(true);
        setIsCreatingItem(false);
        setCurrentItem(item);
        handleCancel(e);
        setIsEditMode(true);
        setIsItemLoading(false);
      }, 300); // Small delay to ensure the dialog shows even for fast operations
    },
    [setIsViewingItemForm, setIsCreatingItem, setCurrentItem, handleCancel, setIsEditMode]
  );

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, item: GlobalItem.AsObject) => {
      e.stopPropagation();
      e.preventDefault();
      setItemToDelete(item);
      setDeleteDialogOpen(true);
    },
    []
  );

  const handleDeleteConfirm = useCallback(() => {
    if (itemToDelete) {
      deleteItem(itemToDelete.itemid);

      // Force refresh data after deletion
      queryClient.invalidateQueries(['itemsByFileId', folder.folderid]);
      queryClient.refetchQueries(['itemsByFileId', folder.folderid], {
        exact: true,
      });

      setCurrentItem(defaultGlobalItem);
      handleCancel({
        stopPropagation: () => {},
      } as React.MouseEvent<HTMLElement>);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  }, [itemToDelete, deleteItem, setCurrentItem, handleCancel, folder.folderid, queryClient]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  }, []);

  const handleAddItemToAssembly = useCallback(
    (item: GlobalItem.AsObject, assyIdx: number) => {
      setIsViewingItemForm(false);

      // Get the specific attribute that was selected
      const selectedAttribute = item.attributesList[assyIdx];

      if (!selectedAttribute) {
        console.error('No attribute found at index', assyIdx, 'for item', item.itemname);
        return;
      }

      // Get default quote groups if the item is quoted
      let quoteGroup = { id: '', quotegroup: '' };

      // If the item is quoted, set a default quote group
      if (item.isquoted) {
        const defaultQuoteGroups = queryClient.getQueryData(['defaultQuoteGroups']);
        if (
          defaultQuoteGroups &&
          Array.isArray(defaultQuoteGroups) &&
          defaultQuoteGroups.length > 0
        ) {
          console.log('Using default quote group:', defaultQuoteGroups[0]);
          quoteGroup = defaultQuoteGroups[0];
        }
      }

      if (currentGlobalAssembly) {
        setCurrentGlobalAssembly(prevAssembly => {
          if (!prevAssembly) return null;

          return {
            ...prevAssembly,
            itemsList: [
              ...(prevAssembly.itemsList || []),
              {
                itemid: item.itemid,
                itemname: item.itemname,
                isfoundryitem: false,
                costs: {
                  uomid: 0,
                  itemcostid: 0,
                  itemid: item.itemid,
                  lastupdated: '',
                  itemunitcost: 0,
                  hourlyproductionrate: 0,
                  itemattributevalueid: 0,
                  installationtime: 0,
                  joblayouttime: 0,
                  materialcleanuptime: 0,
                  nonproductivetime: 0,
                  toolhandlingtime: 0,
                  notes: '',
                  uomname: '',
                  uomvalue: 0,
                },
                itemqtyformula: {
                  itemqty: 1,
                  forevery: 1,
                  takeoffvariabletype: 'point',
                  and: false,
                  or: false,
                  itemid: item.itemid,
                  formulaid: '',
                  isquoted: item.isquoted || false,
                  attributevalueid: selectedAttribute.attributevalueid,
                  quotegroupid: item.isquoted ? quoteGroup.id : '',
                },
                isquoted: item.isquoted || false,
                attribute: selectedAttribute,
                quotegroup: item.isquoted ? quoteGroup : { id: '', quotegroup: '' },
                foundrycategorypath: '', // Add required foundrycategorypath property
              },
            ],
          };
        });
      } else if (currentTakeoffAssembly) {
        setCurrentTakeoffAssembly((prevAssembly: TakeoffAssembly | null) => {
          if (!prevAssembly) {
            return null;
          }

          return {
            ...prevAssembly,
            itemsList: [
              ...(prevAssembly.itemsList || []),
              {
                itemid: item.itemid,
                itemname: item.itemname,
                itemqtyformula: {
                  formulaid: '',
                  itemid: item.itemid,
                  isquoted: item.isquoted || false,
                  attributevalueid: selectedAttribute.attributevalueid,
                  quotegroupid: item.isquoted ? quoteGroup.id : '',
                  itemqty: 1,
                  forevery: 1,
                  takeoffvariabletype: 'point',
                  and: false,
                  or: false,
                },
                isquoted: item.isquoted || false,
                attribute: selectedAttribute,
                quotegroup: item.isquoted ? quoteGroup : { id: '', quotegroup: '' },
                breakout: {
                  breakoutmapid: '',
                  breakoutid: selectedBreakout?.id || '',
                  breakoutname: selectedBreakout?.breakoutname || '',
                  multiplier: selectedBreakout?.multiplier || 1,
                  typicalbreakoutid: (selectedBreakout as any)?._typicalBreakoutId || '',
                },
                isfoundryitem: false, // Add required isfoundryitem property
                foundrycategorypath: '', // Add required foundrycategorypath property
              },
            ],
          };
        });
      }
    },
    [
      setIsViewingItemForm,
      currentGlobalAssembly,
      currentTakeoffAssembly,
      setCurrentGlobalAssembly,
      setCurrentTakeoffAssembly,
      selectedBreakout,
      queryClient,
    ]
  );

  // Create custom outer element for the virtualized list
  const OuterElement = React.forwardRef<HTMLDivElement, HTMLProps<HTMLDivElement>>((props, ref) => (
    <div
      ref={ref}
      {...props}
      style={{
        ...(props.style || {}),
        overflow: processedItems.length * 56 > 400 ? 'auto' : 'visible',
      }}
    />
  ));
  OuterElement.displayName = 'OuterElement';

  // Loading state
  if (!globalItems || isLoading) {
    return <CircularProgress size={24} sx={{ display: 'block', margin: '0 auto' }} />;
  }

  return (
    <>
      {/* Table Header */}
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          backgroundColor:
            theme.palette.mode === 'dark' ? alpha('#000', 0.2) : alpha('#f5f5f5', 0.5),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          px: 2,
          py: 1,
        }}
      >
        <Box sx={{ flex: 1, width: '25%' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Attribute
          </Typography>
        </Box>
        <Box sx={{ flex: 1, width: '25%' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Item Name
          </Typography>
        </Box>
        <Box sx={{ flex: 1, width: '25%' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            UOM
          </Typography>
        </Box>
        <Box sx={{ flex: 1, width: '25%', textAlign: 'right' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Actions
          </Typography>
        </Box>
      </Box>

      {/* Virtualized Table Body */}
      <Paper
        elevation={0}
        sx={{
          height: Math.min(processedItems.length * 56 + 2, 400), // 56px per row + 2px for borders
          width: '100%',
          overflow: 'hidden',
          backgroundColor: 'transparent',
        }}
      >
        <Box
          ref={listContainerRef}
          sx={{
            height: '100%',
            width: '100%',
            overflowY: processedItems.length * 56 + 2 > 400 ? 'auto' : 'visible',
            overflowX: 'hidden',
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch',
            '&::-webkit-scrollbar': {
              width: '8px',
              backgroundColor: alpha(theme.palette.background.paper, 0.1),
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.3),
              },
            },
          }}
        >
          <AutoSizer>
            {({ height, width }: { height: number; width: number }) => {
              // Create a new render function that renders the row directly without using the Row component
              const renderRow = ({ index, style }: { index: number; style: CSSProperties }) => {
                if (index >= processedItems.length) return null;

                const { item, attribute, assyIdx } = processedItems[index];

                // Compute background color for the row
                const bgColor =
                  theme.palette.mode === 'dark'
                    ? index % 2 === 0
                      ? '#1e1e1e'
                      : '#2c2c2c'
                    : index % 2 === 0
                      ? 'transparent'
                      : alpha(theme.palette.action.hover, 0.05);

                const handleAddClick = (e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAddItemToAssembly(item, assyIdx);
                };

                const handleEditClick = (e: React.MouseEvent<any>) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleEdit(e as React.MouseEvent<HTMLButtonElement>, item);
                };

                const handleMoveClick = (e: React.MouseEvent) => {
                  e.stopPropagation();
                  e.preventDefault();
                  moveCurrentItemFile({
                    e: e as React.MouseEvent<HTMLElement>,
                    item,
                    entitytype: 'item',
                  });
                };

                const handleCancelMove = () => {
                  setIsMoveMode(false);
                };

                const handleDeleteClickEvent = (e: React.MouseEvent<any>) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleDeleteClick(e as React.MouseEvent<HTMLButtonElement>, item);
                };

                return (
                  <div style={style}>
                    <TableRow
                      hover
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: `${bgColor} !important`,
                        color: theme.palette.text.primary,
                        transition: 'background-color 0.2s ease',
                        '&.MuiTableRow-hover:hover': {
                          backgroundColor: `${
                            theme.palette.mode === 'dark'
                              ? alpha(theme.palette.primary.main, 0.15)
                              : alpha(theme.palette.primary.main, 0.05)
                          } !important`,
                        },
                        borderBottom: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.2 : 0.1)}`,
                        display: 'flex',
                        width: '100%',
                      }}
                    >
                      <TableCell
                        sx={{
                          color: theme.palette.text.primary,
                          borderBottom: 'none',
                          flex: '1',
                          width: '25%',
                        }}
                      >
                        {attribute.attributevaluename}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: theme.palette.text.primary,
                          borderBottom: 'none',
                          flex: '1',
                          width: '25%',
                        }}
                      >
                        {item.itemname}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: theme.palette.text.primary,
                          borderBottom: 'none',
                          flex: '1',
                          width: '25%',
                        }}
                      >
                        {item.costsList[0]?.uomname || 'N/A'}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: theme.palette.text.primary,
                          textAlign: 'right',
                          borderBottom: 'none',
                          flex: '1',
                          width: '25%',
                          display: 'flex',
                          justifyContent: 'flex-end',
                        }}
                      >
                        {hasAnyPermission([
                          PERMISSIONS.CREATE_ITEMS,
                          PERMISSIONS.EDIT_ITEMS,
                          PERMISSIONS.DELETE_ITEMS,
                          PERMISSIONS.VIEW_ITEMS,
                          PERMISSIONS.EDIT_GLOBAL_ASSEMBLIES,
                          PERMISSIONS.EDIT_TAKEOFF_ASSEMBLIES,
                        ]) ? (
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                              flexWrap: 'nowrap',
                              gap: '4px',
                              justifyContent: 'flex-end',
                            }}
                          >
                            {hasAnyPermission([PERMISSIONS.EDIT_GLOBAL_ASSEMBLIES]) &&
                            currentGlobalAssembly ? (
                              <ActionButton
                                onClick={handleAddClick}
                                icon={<Add fontSize="small" />}
                                tooltip="Add item to global assembly"
                                theme={theme}
                              />
                            ) : hasAnyPermission([PERMISSIONS.EDIT_TAKEOFF_ASSEMBLIES]) &&
                              currentTakeoffAssembly ? (
                              <ActionButton
                                onClick={handleAddClick}
                                icon={<Add fontSize="small" />}
                                tooltip="Add item to takeoff assembly"
                                theme={theme}
                              />
                            ) : null}

                            {hasAnyPermission([PERMISSIONS.EDIT_ITEMS]) ? (
                              <ActionButton
                                onClick={handleEditClick}
                                icon={<Edit fontSize="small" />}
                                tooltip="Edit item properties"
                                theme={theme}
                              />
                            ) : null}

                            {!isMoveMode && hasAnyPermission([PERMISSIONS.EDIT_ITEMS]) ? (
                              <ActionButton
                                onClick={handleMoveClick}
                                icon={<DriveFileMove fontSize="small" />}
                                tooltip="Move item to another location"
                                theme={theme}
                              />
                            ) : hasAnyPermission([PERMISSIONS.EDIT_ITEMS]) ? (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={handleCancelMove}
                                type="reset"
                                sx={{
                                  minWidth: '32px',
                                  padding: '4px 8px',
                                  fontSize: '0.75rem',
                                  color: theme.palette.text.primary,
                                  borderColor: theme.palette.divider,
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    borderColor: theme.palette.primary.main,
                                  },
                                }}
                                aria-label="Cancel move"
                              >
                                Cancel
                              </Button>
                            ) : null}

                            {hasAnyPermission([PERMISSIONS.DELETE_ITEMS]) ? (
                              <ActionButton
                                onClick={handleDeleteClickEvent}
                                icon={<Delete fontSize="small" />}
                                tooltip="Delete item and its contents"
                                theme={theme}
                                colorType="error"
                              />
                            ) : null}
                          </Stack>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  </div>
                );
              };

              return (
                <FixedSizeList
                  height={Math.min(processedItems.length * 56, 400)}
                  width={width}
                  itemCount={processedItems.length}
                  itemSize={56} // Height of each row
                >
                  {renderRow}
                </FixedSizeList>
              );
            }}
          </AutoSizer>
        </Box>
      </Paper>

      <ConfirmationDialog
        open={deleteDialogOpen}
        title="Delete Item"
        message={`Are you sure you want to delete the item "${itemToDelete?.itemname}"? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmText="Delete"
      />

      {/* Loading Dialog */}
      <Dialog
        open={isItemLoading}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'hidden',
          },
        }}
        aria-labelledby="loading-item-dialog"
      >
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <CircularProgress
            sx={{
              color: darkMode ? theme.palette.primary.light : theme.palette.primary.main,
            }}
          />
          <Typography sx={{ color: 'white', mt: 2, fontWeight: 500 }}>
            Loading item "{loadingItemName}"...
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
});
ItemTable.displayName = 'ItemTable';

export default React.memo(ItemTable);
